import React, { useState } from "react";
import { X, Share2, Loader2, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Portal, PortalCapsule } from "@/hooks/usePortal";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CaptureSparkModalProps {
  isOpen: boolean;
  onClose: () => void;
  portal: Portal;
  capsules: PortalCapsule[];
}

const CaptureSparkModal: React.FC<CaptureSparkModalProps> = ({
  isOpen,
  onClose,
  portal,
  capsules,
}) => {
  const [generating, setGenerating] = useState(false);

  const handleGenerateReel = async () => {
    setGenerating(true);
    try {
      // TODO: Implement actual reel generation
      // This would create a video with:
      // - Blurred-to-revealed avatar transition
      // - Capsule audio snippet (with permission)
      // - Aura merge animation
      // - Mode icon
      // - Poetic caption
      
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate generation
      
      toast.success("Reel generated! Ready to share ✨", {
        description: "Your 'Capture Our Spark' reel is ready for Stories/Reels/TikTok",
      });
      
      // TODO: Open share dialog or download
      onClose();
    } catch (error) {
      toast.error("Failed to generate reel");
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const modeLabels: Record<string, string> = {
    serious: "Serious Dating",
    casual: "Fun & Casual",
    deep: "Deep Talk",
    anonymous: "Anonymous First",
  };

  const captions = [
    "We skipped the small talk and opened a Portal together ❤️",
    "No swiping. Just real intention. This is how we connected.",
    "Portal unlocked — the real conversation begins ✨",
    "We opened a Portal and found something real.",
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Capture Our Spark
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Generate a beautiful, shareable reel of your Portal connection
        </p>

        {/* Preview */}
        <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart className="w-6 h-6 text-primary" />
              <span className="text-sm font-medium">{modeLabels[portal.mode]}</span>
            </div>
            <p className="text-xs italic text-muted-foreground">
              "{captions[Math.floor(Math.random() * captions.length)]}"
            </p>
            <p className="text-xs text-muted-foreground">
              Includes: Blurred-to-reveal transition • Capsule audio • Aura merge • Mode icon
            </p>
          </div>
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleGenerateReel}
          disabled={generating}
          className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Share2 className="w-4 h-4 mr-2" />
              Generate & Share Reel
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-3">
          Optimized for Instagram Stories, Reels, and TikTok
        </p>
      </div>
    </div>
  );
};

export default CaptureSparkModal;

